import React, { useRef } from 'react';
import {
  AlertTriangle,
  Check,
  Clock,
  Eye,
  FileUp,
  FileText,
  Gift,
  Image as ImageIcon,
  Loader2,
  Mail,
  MessageSquare,
  Mic,
  Send,
  Sparkles,
  Trash2,
  Users,
  X,
} from 'lucide-react';
import { Card } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { formatDate } from '../adminFormatters';
import { normalizeEmailAddress } from '../emailHealthSignals';
import EmailRichTextEditor from '../email/EmailRichTextEditor';

const AUDIENCE_LABELS = {
  everyone: 'Everyone',
  admins: 'Admin',
  single_email: 'Multiple people',
  members_all: 'Members - All',
  members_monthly: 'Members - Monthly',
  members_annual: 'Members - Annual',
  former_members: 'Former Members',
  founding40_active: 'Founding 40 - Active',
  founding40_all: 'Founding 40 - All',
  guest_guides_active: 'Guide Partners - Active',
  guest_guides_former: 'Guide Partners - Former',
  guide_prospects_all: 'Guide Partners - All Prospects',
  guide_prospects_invited: 'Guide Partners - Invited',
  guide_prospects_not_invited: 'Guide Partners - Not Yet Invited',
  guide_prospects_stale: 'Guide Partners - Invited, No Response 30+ Days',
  advisory_partners: 'Advisory Partners - Active',
  moderators_active: 'Moderators - Active',
  moderators_former: 'Moderators - Former',
  beta_testers_active: 'Beta Testers - Active',
  beta_testers_former: 'Beta Testers - Former',
  guests_active: 'Guests - Active',
  prospects: 'Prospects',
};

const GUIDE_PROSPECT_AUDIENCES = new Set([
  'guide_prospects_all',
  'guide_prospects_invited',
  'guide_prospects_not_invited',
  'guide_prospects_stale',
]);

const parseCustomRecipientEmails = (value) => {
  const seen = new Set();
  return String(value || '')
    .split(/[\s,;]+/)
    .map((email) => email.trim())
    .filter((email) => {
      const normalized = normalizeEmailAddress(email);
      if (!normalized || seen.has(normalized)) return false;
      seen.add(normalized);
      return true;
    });
};

const EmailTab = ({
  emailContent,
  setEmailContent,
  emailHeroUploading,
  handleUploadEmailHero,
  sending,
  emailTestSending = false,
  emailScheduling = false,
  emailScheduleAt = '',
  setEmailScheduleAt,
  emailPreview,
  emailPreviewOpen,
  setEmailPreviewOpen,
  previewingEmail,
  recentEmails,
  loading,
  emailAnalytics = null,
  emailAnalyticsLoading = false,
  deliveryIssues,
  deliveryIssueCounts,
  deliveryIssuesLoading,
  deliveryIssuesImporting,
  deliveryIssuesLastImportedAt,
  handleImportDeliveryIssues,
  handlePreviewEmail,
  handleSendTestEmail,
  handleScheduleEmail,
  handleSendEmail,
  scheduledEmails = [],
  scheduledEmailsLoading = false,
  handleCancelScheduledEmail,
  emailDraftAssist,
  setEmailDraftAssist,
  generatingEmailDraft,
  handleGenerateEmailDraft,
  savedEmailDrafts = [],
  savedEmailDraftsLoading = false,
  emailDraftSaving = false,
  handleSaveEmailDraft,
  handleLoadEmailDraft,
  handleDeleteEmailDraft,
  savedEmailTemplates = [],
  savedEmailTemplatesLoading = false,
  emailTemplateSaving = false,
  handleSaveEmailTemplate,
  handleLoadEmailTemplate,
  handleDeleteEmailTemplate,
  activeZone,
  onOpenTool,
}) => {
  const fileInputRef = useRef(null);
  const heroFileInputRef = useRef(null);
  const issueCount = deliveryIssueCounts?.all || deliveryIssues?.length || 0;
  const currentAudienceLabel = AUDIENCE_LABELS[emailContent.audience] || 'Custom audience';
  const isGuidePartnersZone = activeZone?.id === 'guide-partners';
  const isDadPartnersZone = activeZone?.id === 'heart-work';
  const isAdvisoryPartnersZone = activeZone?.id === 'advisory-partners';
  const isSupportPartnersZone = activeZone?.id === 'support-partners';
  const operatorTitle = isGuidePartnersZone
    ? 'Coordinate guide partner follow-up without losing the human thread'
    : isDadPartnersZone
      ? 'Send next-step care only after the dad relationship move is clear'
      : isAdvisoryPartnersZone
        ? 'Send advisory follow-up only after the ask or update is concrete'
        : isSupportPartnersZone
          ? 'Send supporter follow-up only after the trust move is clear'
      : 'Send next-step outreach only after the prospect move is clear';
  const operatorDetail = isGuidePartnersZone
    ? 'Use Email here to support the Guide Partners room: clean up delivery problems, send the right next note, and keep long-term cultivation from feeling mechanical.'
    : isDadPartnersZone
      ? 'Use Email after Dad Partners or Feedback has made the next care move obvious. This tool should turn a clear relationship decision into a send, not replace the human judgment that comes first.'
      : isAdvisoryPartnersZone
        ? 'Use Email here for advisory follow-up, private updates, and clear next asks once the relationship move is already obvious.'
        : isSupportPartnersZone
          ? 'Use Email here for supporter trust-building, thank-you follow-up, and mission updates once you know exactly who should hear from you next.'
      : 'Use Email after Prospects or Guest Passes has made the next move obvious. This tool should turn a clear relationship decision into a send, not replace the relational judgment that comes first.';
  const helperText = isGuidePartnersZone
    ? 'Best for invitations, reminders, and sequence follow-up once you know which guide partners should hear from you next.'
    : isDadPartnersZone
      ? 'Best for dad check-ins, encouragement, and member follow-up once you already know which dads should hear from you next.'
      : isAdvisoryPartnersZone
        ? 'Best for private advisory asks, thoughtful updates, and one clear next move once the context is already settled.'
        : isSupportPartnersZone
          ? 'Best for supporter thank-yous, mission updates, and trust-building follow-up once the next relational step is already clear.'
      : 'Best for warm-prospect or Founding 40 follow-up once you already know who should hear from Dad Partners next.';
  const supportsAiDraftAssist = (
    isGuidePartnersZone
    || isDadPartnersZone
    || isAdvisoryPartnersZone
    || isSupportPartnersZone
    || activeZone?.id === 'communication'
  );
  const isSingleEmailAudience = emailContent.audience === 'single_email';
  const isGuideProspectAudience = GUIDE_PROSPECT_AUDIENCES.has(emailContent.audience);
  const excludedEmails = parseCustomRecipientEmails(emailContent.excludedEmails);
  const customRecipientEmails = isSingleEmailAudience ? parseCustomRecipientEmails(emailContent.singleEmailAddress) : [];
  const singleEmailDeliveryIssue = isSingleEmailAudience
    ? (deliveryIssues || []).find((issue) => {
        const issueEmail = normalizeEmailAddress(issue?.email);
        if (!issueEmail || !customRecipientEmails.includes(issueEmail)) return false;
        const category = String(issue?.latest_status_category || '').trim().toLowerCase();
        const status = String(issue?.latest_status || '').trim().toLowerCase();
        const exclude = issue?.exclude_from_automation;
        if (typeof exclude === 'boolean') return exclude;
        return status === 'suppressed' || status === 'complained' || category === 'hard_bounce' || status === 'bounced';
      }) || null
    : null;
  const singleEmailBlockingLabel = singleEmailDeliveryIssue?.latest_issue_label || 'Delivery issue';
  const singleEmailBlockingSummary =
    singleEmailDeliveryIssue?.latest_issue_summary ||
    'This address should not be emailed automatically right now.';
  const placeholderWarnings = emailPreview?.placeholder_warnings || [];
  const hasPlaceholderWarnings = placeholderWarnings.length > 0;
  const sendBlocked = sending || Boolean(singleEmailDeliveryIssue) || hasPlaceholderWarnings;
  const previewRecipients = emailPreview?.recipients || [];
  const previewRecipientCount = emailPreview?.recipient_count || 0;
  const heldBack = emailPreview?.held_back || [];
  const heldBackCount = emailPreview?.held_back_count || 0;
  const analyticsAudiences = emailAnalytics?.by_audience || [];

  return (
    <div className="space-y-6">
      <Card className="border-cyan-200 bg-gradient-to-br from-[#eef9fc] via-white to-[#eef6fb] p-6 dark:border-aqua/20 dark:bg-gradient-to-br dark:from-[#15232b] dark:via-[#141c24] dark:to-[#111920]">
        <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
          <div className="max-w-3xl">
            <p className="text-[11px] font-semibold uppercase tracking-[0.26em] text-cyan-800 dark:text-aqua">
              {isGuidePartnersZone
                ? 'Guide Partners flow'
                : isDadPartnersZone
                  ? 'Dad Partners flow'
                  : isAdvisoryPartnersZone
                    ? 'Advisory Partners flow'
                    : isSupportPartnersZone
                      ? 'Support Partners flow'
                      : 'Prospects flow'}
            </p>
            <h3 className="mt-3 text-2xl font-semibold text-slate-900 dark:text-white">
              {operatorTitle}
            </h3>
            <p className="mt-3 text-sm leading-6 text-slate-600 dark:text-slate-200">
              {operatorDetail}
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              {isGuidePartnersZone ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenTool?.('guestguides', 'guide-partners')}
                  className="border-slate-200 bg-white text-slate-900 hover:bg-slate-50 dark:border-[#35505f] dark:bg-[#22313c] dark:text-white dark:hover:bg-[#2a3a46]"
                >
                  <Mic className="mr-2 h-4 w-4" />
                  Review guide partners
                </Button>
              ) : isDadPartnersZone ? (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => onOpenTool?.('members', 'support-dads')}
                    className="border-slate-200 bg-white text-slate-900 hover:bg-slate-50 dark:border-[#35505f] dark:bg-[#22313c] dark:text-white dark:hover:bg-[#2a3a46]"
                  >
                    <Users className="mr-2 h-4 w-4" />
                    Review dad partners
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => onOpenTool?.('feedback', 'listen-learn')}
                    className="border-slate-200 bg-white text-slate-900 hover:bg-slate-50 dark:border-[#35505f] dark:bg-[#22313c] dark:text-white dark:hover:bg-[#2a3a46]"
                  >
                    <MessageSquare className="mr-2 h-4 w-4" />
                    Review feedback
                  </Button>
                </>
              ) : isAdvisoryPartnersZone ? (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => onOpenTool?.('advisorypartneraccounts', 'advisory-partners')}
                    className="border-slate-200 bg-white text-slate-900 hover:bg-slate-50 dark:border-[#35505f] dark:bg-[#22313c] dark:text-white dark:hover:bg-[#2a3a46]"
                  >
                    <Users className="mr-2 h-4 w-4" />
                    Review advisory partners
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => onOpenTool?.('advisorycontent', 'advisory-partners')}
                    className="border-slate-200 bg-white text-slate-900 hover:bg-slate-50 dark:border-[#35505f] dark:bg-[#22313c] dark:text-white dark:hover:bg-[#2a3a46]"
                  >
                    <MessageSquare className="mr-2 h-4 w-4" />
                    Review advisory content
                  </Button>
                </>
              ) : isSupportPartnersZone ? (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => onOpenTool?.('supportpartneraccounts', 'support-partners')}
                    className="border-slate-200 bg-white text-slate-900 hover:bg-slate-50 dark:border-[#35505f] dark:bg-[#22313c] dark:text-white dark:hover:bg-[#2a3a46]"
                  >
                    <Users className="mr-2 h-4 w-4" />
                    Review support partners
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => onOpenTool?.('supportcontent', 'support-partners')}
                    className="border-slate-200 bg-white text-slate-900 hover:bg-slate-50 dark:border-[#35505f] dark:bg-[#22313c] dark:text-white dark:hover:bg-[#2a3a46]"
                  >
                    <MessageSquare className="mr-2 h-4 w-4" />
                    Review support content
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => onOpenTool?.('members', 'reach-dads')}
                    className="border-slate-200 bg-white text-slate-900 hover:bg-slate-50 dark:border-[#35505f] dark:bg-[#22313c] dark:text-white dark:hover:bg-[#2a3a46]"
                  >
                    <Users className="mr-2 h-4 w-4" />
                    Review prospects
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => onOpenTool?.('guestpasses', 'reach-dads')}
                    className="border-slate-200 bg-white text-slate-900 hover:bg-slate-50 dark:border-[#35505f] dark:bg-[#22313c] dark:text-white dark:hover:bg-[#2a3a46]"
                  >
                    <Gift className="mr-2 h-4 w-4" />
                    Check guest passes
                  </Button>
                </>
              )}
            </div>
          </div>

          <div className="grid min-w-full gap-3 sm:grid-cols-3 xl:min-w-[440px] xl:max-w-[520px]">
            <div className="rounded-2xl border border-cyan-200 bg-white/90 p-4 dark:border-[#35505f] dark:bg-[#22313c]">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">Current audience</p>
              <p className="mt-3 text-lg font-semibold text-slate-900 dark:text-white">{currentAudienceLabel}</p>
              <p className="mt-2 text-sm text-slate-600 dark:text-slate-200">The starting segment loaded for this zone</p>
            </div>
            <div className="rounded-2xl border border-amber-200 bg-white/90 p-4 dark:border-[#4d4a3d] dark:bg-[#2b3138]">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">Delivery issues</p>
              <p className="mt-3 text-3xl font-semibold text-slate-900 dark:text-white">{deliveryIssuesLoading ? '—' : issueCount}</p>
              <p className="mt-2 text-sm text-slate-600 dark:text-slate-200">People whose next send may need cleanup first</p>
            </div>
            <div className="rounded-2xl border border-emerald-200 bg-white/90 p-4 dark:border-[#34514a] dark:bg-[#223139]">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">Recent sends</p>
              <p className="mt-3 text-3xl font-semibold text-slate-900 dark:text-white">{recentEmails.length}</p>
              <p className="mt-2 text-sm text-slate-600 dark:text-slate-200">Latest sends visible in this tool right now</p>
            </div>
          </div>
        </div>
      </Card>

      <Card className="border-slate-200 bg-white p-6 dark:border-[#343441] dark:bg-[#1e1e24]">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Delivery history</h3>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
              Owner-scoped send activity from the last {emailAnalytics?.days || 30} days.
            </p>
          </div>
          <Badge className="w-fit border-slate-200 bg-slate-100 text-slate-700 dark:border-[#3d414d] dark:bg-[#252631] dark:text-slate-200">
            Read-only
          </Badge>
        </div>
        {emailAnalyticsLoading ? (
          <div className="mt-5 flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading delivery history...
          </div>
        ) : (
          <>
            <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-6">
              {[
                ['Campaign sends', emailAnalytics?.total_campaign_sends ?? 0],
                ['Recipients attempted', emailAnalytics?.total_recipients ?? 0],
                ['Successful', emailAnalytics?.successful_sends ?? 0],
                ['Queued / failed', `${emailAnalytics?.queued_sends ?? 0} / ${emailAnalytics?.failed_sends ?? 0}`],
                ['Scheduled sends', emailAnalytics?.scheduled_sends ?? 0],
                ['Self-tests', emailAnalytics?.test_sends ?? 0],
              ].map(([label, value]) => (
                <div key={label} className="rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-[#343441] dark:bg-[#16161a]">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">{label}</p>
                  <p className="mt-2 text-2xl font-semibold text-slate-900 dark:text-white">{value}</p>
                </div>
              ))}
            </div>
            {analyticsAudiences.length > 0 && (
              <div className="mt-5 overflow-hidden rounded-xl border border-slate-200 dark:border-[#343441]">
                <div className="grid grid-cols-[minmax(0,1fr)_auto_auto] gap-3 bg-slate-50 px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500 dark:bg-[#16161a] dark:text-slate-400">
                  <span>Audience</span><span>Sends</span><span>Recipients</span>
                </div>
                {analyticsAudiences.map((row) => (
                  <div key={`${row.recipient_type}-${row.tier || 'all'}`} className="grid grid-cols-[minmax(0,1fr)_auto_auto] gap-3 border-t border-slate-200 px-4 py-3 text-sm text-slate-700 dark:border-[#343441] dark:text-slate-200">
                    <span>{AUDIENCE_LABELS[row.recipient_type] || row.recipient_type}{row.tier ? ` · ${row.tier}` : ''}</span>
                    <span>{row.send_count ?? 0}</span>
                    <span>{row.recipient_count ?? 0}</span>
                  </div>
                ))}
              </div>
            )}
            <p className="mt-4 text-xs leading-5 text-slate-500 dark:text-slate-400">
              Delivery history only — open, click, and conversion tracking are not connected.
            </p>
          </>
        )}
      </Card>

      <Card className="border-slate-200 bg-white p-6 dark:border-[#343441] dark:bg-[#1e1e24]">
        <h3 className="mb-4 flex items-center gap-2 text-lg font-semibold text-slate-900 dark:text-white">
          <Mail className="w-5 h-5 text-aqua" />
          Compose Email
        </h3>
        <div className="mb-5 rounded-xl border border-cyan-200 bg-cyan-50/70 p-4 dark:border-aqua/20 dark:bg-[#132831]">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <p className="text-sm font-semibold text-slate-900 dark:text-white">Saved drafts</p>
              <p className="mt-1 text-xs leading-5 text-slate-600 dark:text-slate-300">Save unfinished work here so you can return without sending anything.</p>
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={handleSaveEmailDraft}
              disabled={emailDraftSaving}
              className="shrink-0 gap-2 border-cyan-300 bg-white text-slate-900 hover:bg-cyan-50 dark:border-aqua/30 dark:bg-[#16161a] dark:text-white"
            >
              {emailDraftSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              {emailDraftSaving ? 'Saving...' : emailContent.draftId ? 'Update draft' : 'Save draft'}
            </Button>
          </div>
          {savedEmailDraftsLoading ? (
            <div className="mt-3 flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading drafts...
            </div>
          ) : savedEmailDrafts.length ? (
            <div className="mt-3 space-y-2">
              {savedEmailDrafts.map((draft) => (
                <div key={draft.draft_id} className="flex flex-col gap-2 rounded-lg border border-cyan-200 bg-white px-3 py-2.5 dark:border-[#34505a] dark:bg-[#16161a] sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-slate-900 dark:text-white">{draft.subject || 'Untitled draft'}</p>
                    <p className="mt-1 text-xs text-slate-600 dark:text-slate-300">
                      {AUDIENCE_LABELS[draft.recipient_type] || draft.recipient_type} · {formatDate(draft.updated_at)}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => handleLoadEmailDraft?.(draft.draft_id)}
                      className="h-8 border-slate-200 bg-white px-3 text-xs text-slate-900 hover:bg-slate-50 dark:border-[#343441] dark:bg-[#22222b] dark:text-white"
                    >
                      Load
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      aria-label={`Delete ${draft.subject || 'untitled draft'}`}
                      onClick={() => handleDeleteEmailDraft?.(draft.draft_id)}
                      className="h-8 border-rose-200 bg-white px-2 text-rose-700 hover:bg-rose-50 dark:border-rose-500/30 dark:bg-[#22222b] dark:text-rose-300"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="mt-3 text-xs text-slate-600 dark:text-slate-300">No saved drafts in this zone yet.</p>
          )}
        </div>
        <div className="mb-5 rounded-xl border border-violet-200 bg-violet-50/70 p-4 dark:border-violet-500/20 dark:bg-[#241b32]">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex min-w-0 items-start gap-3">
              <FileText className="mt-0.5 h-5 w-5 shrink-0 text-violet-700 dark:text-violet-300" />
              <div>
                <p className="text-sm font-semibold text-slate-900 dark:text-white">Reusable email templates</p>
                <p className="mt-1 text-xs leading-5 text-slate-600 dark:text-slate-300">
                  Save reviewed copy and safe audience defaults for future sends. Personal recipient addresses are never stored in a template.
                </p>
              </div>
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={handleSaveEmailTemplate}
              disabled={
                emailTemplateSaving
                || !emailContent.templateName?.trim()
                || !emailContent.subject?.trim()
                || !emailContent.body?.trim()
                || isSingleEmailAudience
              }
              className="shrink-0 gap-2 border-violet-300 bg-white text-slate-900 hover:bg-violet-50 dark:border-violet-400/30 dark:bg-[#16161a] dark:text-white"
            >
              {emailTemplateSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              {emailTemplateSaving ? 'Saving...' : emailContent.templateId ? 'Update template' : 'Save as template'}
            </Button>
          </div>
          <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
            <Input
              value={emailContent.templateName || ''}
              onChange={(e) => setEmailContent({ ...emailContent, templateName: e.target.value })}
              placeholder="Template name, e.g. Monthly member check-in"
              className="border-violet-200 bg-white text-slate-900 placeholder:text-slate-500 dark:border-violet-400/30 dark:bg-[#16161a] dark:text-white dark:placeholder:text-slate-400"
            />
            {isSingleEmailAudience ? (
              <p className="text-xs text-amber-800 dark:text-amber-200">Choose a reusable audience before saving a template.</p>
            ) : null}
          </div>
          {savedEmailTemplatesLoading ? (
            <div className="mt-3 flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading templates...
            </div>
          ) : savedEmailTemplates.length ? (
            <div className="mt-3 space-y-2">
              {savedEmailTemplates.map((template) => (
                <div key={template.template_id} className="flex flex-col gap-2 rounded-lg border border-violet-200 bg-white px-3 py-2.5 dark:border-violet-400/20 dark:bg-[#16161a] sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-slate-900 dark:text-white">{template.name || 'Untitled template'}</p>
                    <p className="mt-1 truncate text-xs text-slate-600 dark:text-slate-300">
                      {template.subject || 'No subject'} · {AUDIENCE_LABELS[template.recipient_type] || template.recipient_type} · {formatDate(template.updated_at)}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => handleLoadEmailTemplate?.(template.template_id)}
                      className="h-8 border-slate-200 bg-white px-3 text-xs text-slate-900 hover:bg-slate-50 dark:border-[#343441] dark:bg-[#22222b] dark:text-white"
                    >
                      Load
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      aria-label={`Delete ${template.name || 'untitled template'}`}
                      onClick={() => handleDeleteEmailTemplate?.(template.template_id)}
                      className="h-8 border-rose-200 bg-white px-2 text-rose-700 hover:bg-rose-50 dark:border-rose-500/30 dark:bg-[#22222b] dark:text-rose-300"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="mt-3 text-xs text-slate-600 dark:text-slate-300">No saved templates in this zone yet.</p>
          )}
        </div>
        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-white">Audience</label>
            <select
              value={emailContent.audience}
              onChange={(e) => setEmailContent({ ...emailContent, audience: e.target.value })}
              className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-slate-900 shadow-sm dark:border-[#3b3b48] dark:bg-[#16161a] dark:text-white"
            >
              <option value="everyone">Everyone (exclude prospects, former, trial)</option>
              <option value="admins">Admin</option>
              <option value="single_email">Multiple people</option>
              <option value="members_all">Members — All (active, monthly+annual)</option>
              <option value="members_monthly">Members — Monthly</option>
              <option value="members_annual">Members — Annual</option>
              <option value="former_members">Former Members</option>
              <option value="founding40_active">Founding 40 — Active</option>
              <option value="founding40_all">Founding 40 — All</option>
              <option value="guest_guides_active">Guide Partners — Active</option>
              <option value="guest_guides_former">Guide Partners — Former</option>
              <option value="guide_prospects_all">Guide Partners — All Prospects (pipeline)</option>
              <option value="guide_prospects_invited">Guide Partners — Invited (pipeline)</option>
              <option value="guide_prospects_not_invited">Guide Partners — Not Yet Invited (pipeline)</option>
              <option value="guide_prospects_stale">Guide Partners — Invited, No Response 30+ Days</option>
              <option value="advisory_partners">Advisory Partners — Active</option>
              <option value="moderators_active">Moderators — Active</option>
              <option value="moderators_former">Moderators — Former</option>
              <option value="beta_testers_active">Beta Testers — Active</option>
              <option value="beta_testers_former">Beta Testers — Former</option>
              <option value="guests_active">Guests — Active</option>
              <option value="prospects">Prospects</option>
            </select>
          </div>
          {isSingleEmailAudience ? (
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-white">Recipient emails</label>
              <Textarea
                value={emailContent.singleEmailAddress || ''}
                onChange={(e) => setEmailContent({ ...emailContent, singleEmailAddress: e.target.value })}
                placeholder={'name@example.com, name2@example.com\nname3@example.com'}
                className="min-h-[96px] border-slate-300 bg-white text-slate-900 placeholder:text-slate-500 shadow-sm dark:border-[#3b3b48] dark:bg-[#16161a] dark:text-white dark:placeholder:text-gray-500"
              />
              <p className="mt-2 text-xs text-slate-600 dark:text-slate-300">
                Separate addresses with commas, spaces, or new lines.
                {customRecipientEmails.length > 0 ? ` ${customRecipientEmails.length} recipient${customRecipientEmails.length === 1 ? '' : 's'} ready.` : ''}
              </p>
            </div>
          ) : null}
          {!isSingleEmailAudience ? (
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-white">
                Exclude these people (optional)
              </label>
              <Textarea
                value={emailContent.excludedEmails || ''}
                onChange={(e) => setEmailContent({ ...emailContent, excludedEmails: e.target.value })}
                placeholder={'already-wrote-to@example.com, someone-else@example.com'}
                className="min-h-[72px] border-slate-300 bg-white text-slate-900 placeholder:text-slate-500 shadow-sm dark:border-[#3b3b48] dark:bg-[#16161a] dark:text-white dark:placeholder:text-gray-500"
              />
              <p className="mt-2 text-xs text-slate-600 dark:text-slate-300">
                Anyone listed here is removed from this send, even if they match the audience.
                Separate addresses with commas, spaces, or new lines.
                {excludedEmails.length > 0 ? ` ${excludedEmails.length} address${excludedEmails.length === 1 ? '' : 'es'} will be suppressed.` : ''}
                {excludedEmails.length > 0 && emailPreview && typeof emailPreview.excluded_count === 'number'
                  ? ` Preview matched ${emailPreview.excluded_count} of them in this audience.`
                  : ''}
              </p>
            </div>
          ) : null}
          {isGuideProspectAudience ? (
            <div className="rounded-xl border border-cyan-200 bg-cyan-50/80 px-4 py-3 text-sm text-slate-700 dark:border-aqua/25 dark:bg-[#14303a] dark:text-slate-100">
              This audience reads the Guide Partners pipeline, not user accounts, so it reaches prospects
              who never created a login. Names come from the pipeline record, so {'{{first_name}}'} works.
              Sending also logs the note to each guide&rsquo;s outreach history and last-contacted date.
            </div>
          ) : null}
          {singleEmailDeliveryIssue ? (
            <div className="rounded-xl border border-rose-200 bg-rose-50/80 px-4 py-3 text-sm text-rose-900 dark:border-rose-500/25 dark:bg-rose-500/10 dark:text-rose-100">
              <p className="font-medium">{singleEmailBlockingLabel}</p>
              <p className="mt-1">{singleEmailBlockingSummary}</p>
            </div>
          ) : null}
          <div className="rounded-xl border border-slate-200 bg-slate-50/90 px-4 py-3 text-sm text-slate-700 dark:border-[#343441] dark:bg-[#22222b] dark:text-slate-100">
            {isSingleEmailAudience
              ? 'Best for a one-off note to a small hand-entered list without needing existing user records.'
              : helperText}
          </div>
          {supportsAiDraftAssist ? (
            <div className="rounded-2xl border border-cyan-300 bg-cyan-100/95 p-4 dark:border-aqua/30 dark:bg-[#14303a]">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                <div className="max-w-2xl">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-cyan-800 dark:text-aqua">AI Draft Assist</p>
                  <p className="mt-2 text-sm leading-6 text-slate-900 dark:text-slate-100">
                    Give the goal and the details that changed. If you already started the subject or body, the draft will tighten that copy instead of starting over.
                  </p>
                </div>
                <Button
                  type="button"
                  onClick={handleGenerateEmailDraft}
                  disabled={generatingEmailDraft}
                  className="bg-aqua text-charcoal hover:bg-aqua-dark gap-2"
                >
                  {generatingEmailDraft ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                  {generatingEmailDraft ? 'Drafting...' : 'Draft with AI'}
                </Button>
              </div>
              <div className="mt-4 grid gap-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-800 dark:text-white">What is this email for?</label>
                  <Input
                    value={emailDraftAssist.goal}
                    onChange={(e) => setEmailDraftAssist({ ...emailDraftAssist, goal: e.target.value })}
                    placeholder={isGuidePartnersZone ? 'Example: Let Guide Partners know about the scheduling change and what to do next.' : 'Example: Tell the Founding 40 dads about the schedule change and what it means.'}
                    className="border-slate-300 bg-white text-slate-900 placeholder:text-slate-500 dark:border-[#3b3b48] dark:bg-[#16161a] dark:text-white dark:placeholder:text-slate-400"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-800 dark:text-white">Key details to mention</label>
                  <Textarea
                    value={emailDraftAssist.details}
                    onChange={(e) => setEmailDraftAssist({ ...emailDraftAssist, details: e.target.value })}
                    placeholder="Dates, changes, requests, tone notes, or anything Kevin already knows he wants included."
                    className="min-h-[120px] border-slate-300 bg-white text-slate-900 placeholder:text-slate-500 dark:border-[#3b3b48] dark:bg-[#16161a] dark:text-white dark:placeholder:text-slate-400"
                  />
                </div>
              </div>
            </div>
          ) : null}
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-white">Subject</label>
            <Input
              value={emailContent.subject}
              onChange={(e) => setEmailContent({ ...emailContent, subject: e.target.value })}
              placeholder="Email subject..."
              className="border-slate-300 bg-white text-slate-900 placeholder:text-slate-500 shadow-sm dark:border-[#3b3b48] dark:bg-[#16161a] dark:text-white dark:placeholder:text-slate-400"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-white">Body</label>
            <EmailRichTextEditor
              value={emailContent.body}
              onChange={(nextValue) => setEmailContent({ ...emailContent, body: nextValue })}
            />
            <p className="mt-2 text-xs text-slate-600 dark:text-slate-300">
              Paste formatted text here. The editor keeps basic email-safe formatting like headings, lists, emphasis, and links.
            </p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-slate-50/90 p-4 dark:border-[#343441] dark:bg-[#22222b]">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <label className="block text-sm font-medium text-slate-800 dark:text-white">Hero image (optional)</label>
                <p className="mt-1 max-w-2xl text-xs leading-5 text-slate-600 dark:text-slate-300">
                  Upload a JPEG, PNG, or WebP image up to 3MB. It will appear at the top of the email and remain part of the review before sending.
                </p>
              </div>
              <input
                ref={heroFileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={(event) => {
                  const [file] = event.target.files || [];
                  handleUploadEmailHero?.(file);
                  event.target.value = '';
                }}
              />
              {!emailContent.heroImageUrl ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => heroFileInputRef.current?.click()}
                  disabled={emailHeroUploading}
                  className="shrink-0 gap-2 border-slate-300 bg-white text-slate-900 hover:bg-slate-100 dark:border-[#45505b] dark:bg-[#16161a] dark:text-white dark:hover:bg-[#20252b]"
                >
                  {emailHeroUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-4 w-4" />}
                  {emailHeroUploading ? 'Uploading...' : 'Upload image'}
                </Button>
              ) : null}
            </div>
            {emailContent.heroImageUrl ? (
              <div className="mt-4 flex flex-col gap-3 rounded-lg border border-slate-200 bg-white p-3 dark:border-[#343441] dark:bg-[#16161a] sm:flex-row sm:items-center sm:justify-between">
                <div className="flex min-w-0 items-center gap-3">
                  <img
                    src={emailContent.heroImageUrl}
                    alt="Email hero preview"
                    className="h-16 w-28 rounded-md object-cover"
                  />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-900 dark:text-white">Hero image ready for review</p>
                    <p className="truncate text-xs text-slate-500 dark:text-slate-400">The image will be included in the preview and any later send.</p>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setEmailContent((prev) => ({ ...prev, heroImageUrl: '' }))}
                  className="shrink-0 gap-2 border-slate-300 bg-white text-slate-900 hover:bg-slate-100 dark:border-[#45505b] dark:bg-[#16161a] dark:text-white dark:hover:bg-[#20252b]"
                >
                  <X className="h-4 w-4" />
                  Remove
                </Button>
              </div>
            ) : null}
          </div>
          {issueCount > 0 ? (
            <div className="rounded-xl border border-amber-200 bg-amber-50/80 px-4 py-3 text-sm text-amber-900 dark:border-amber-500/25 dark:bg-amber-500/10 dark:text-amber-100">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <p>
                  {issueCount} imported delivery issue{issueCount === 1 ? '' : 's'} are still in view. Clean those records before trusting automation or assuming the right people actually received this note.
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => onOpenTool?.(
                      isGuidePartnersZone ? 'guestguides' : 'members',
                      isGuidePartnersZone ? 'guide-partners' : isDadPartnersZone ? 'support-dads' : 'reach-dads',
                    )}
                    className="border-amber-200 bg-white text-amber-900 hover:bg-amber-100 dark:border-amber-500/25 dark:bg-black/20 dark:text-amber-100 dark:hover:bg-black/30"
                  >
                    Review records
                  </Button>
                </div>
              </div>
            </div>
          ) : null}
          <Button onClick={handlePreviewEmail} disabled={previewingEmail || sending} className="bg-aqua hover:bg-aqua-dark text-charcoal gap-2">
            {previewingEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
            {previewingEmail ? 'Loading preview...' : 'Review Email'}
          </Button>
        </div>
      </Card>

      <Dialog open={emailPreviewOpen} onOpenChange={setEmailPreviewOpen}>
        <DialogContent className="max-h-[90vh] max-w-5xl overflow-y-auto border-slate-200 bg-white dark:border-[#343441] dark:bg-[#16161a]">
          <DialogHeader>
            <DialogTitle className="text-slate-900 dark:text-white">Review email before sending</DialogTitle>
            <DialogDescription className="text-slate-600 dark:text-slate-300">
              This is the rendered email template and the current recipient preview. If anything looks off, close this and edit first.
            </DialogDescription>
          </DialogHeader>

          {hasPlaceholderWarnings ? (
            <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-amber-950 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-100">
              <p className="font-semibold">Fix unsupported placeholders before sending</p>
              <p className="mt-1 text-sm">{placeholderWarnings.join(', ')}</p>
            </div>
          ) : null}

          <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
            <div className="space-y-4">
              <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 dark:border-[#343441] dark:bg-[#11161d]">
                <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">Subject</p>
                <p className="mt-2 text-base font-semibold text-slate-900 dark:text-white">
                  {emailPreview?.subject_preview || emailContent.subject || 'No subject yet'}
                </p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white p-2 dark:border-[#343441] dark:bg-[#11161d]">
                {emailPreview?.html_preview ? (
                  <iframe
                    title="Email preview"
                    srcDoc={emailPreview.html_preview}
                    className="h-[640px] w-full rounded-lg border-0 bg-white"
                  />
                ) : (
                  <div className="flex h-[320px] items-center justify-center text-sm text-slate-500 dark:text-slate-300">
                    No preview available yet.
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-4">
              <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-4 dark:border-[#343441] dark:bg-[#11161d]">
                <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">Audience</p>
                <p className="mt-2 text-sm font-medium text-slate-900 dark:text-white">{currentAudienceLabel}</p>
                <p className="mt-3 text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">Recipients</p>
                <p className="mt-2 text-2xl font-semibold text-slate-900 dark:text-white">
                  {previewRecipientCount}
                  {heldBackCount > 0 ? (
                    <span className="text-base font-medium text-amber-700 dark:text-amber-300">
                      {' \u00b7 '}{heldBackCount} held back
                    </span>
                  ) : null}
                </p>
                {heldBackCount > 0 ? (
                  <p className="mt-1 text-sm font-medium text-amber-800 dark:text-amber-200">
                    {heldBackCount} {heldBackCount === 1 ? 'person is' : 'people are'} already in a campaign and will not
                    receive this. They are listed below.
                  </p>
                ) : null}
                <p className="mt-1 text-sm text-slate-700 dark:text-slate-200">
                  {previewRecipientCount > previewRecipients.length
                    ? `Showing the first ${previewRecipients.length}.`
                    : 'Showing the current send list.'}
                </p>
              </div>

              {heldBackCount > 0 ? (
                <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-4 dark:border-amber-500/30 dark:bg-amber-500/10">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-700 dark:text-amber-300" />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-900 dark:text-white">
                        Held back — already in a campaign
                      </p>
                      <p className="mt-1 text-xs leading-5 text-slate-700 dark:text-slate-200">
                        Check whether these campaigns are real or leftovers. A campaign that never sends will keep
                        holding these people back every time.
                      </p>
                    </div>
                  </div>
                  <div className="mt-3 max-h-[260px] space-y-2 overflow-y-auto">
                    {heldBack.map((person) => (
                      <div
                        key={person.guide_id || person.email}
                        className="rounded-lg border border-amber-200 bg-white px-3 py-2 text-sm dark:border-amber-500/20 dark:bg-[#16161a]"
                      >
                        <p className="font-medium text-slate-900 dark:text-white">
                          {person.name || person.email || 'Unnamed guide'}
                        </p>
                        {person.name && person.email ? (
                          <p className="text-xs text-slate-600 dark:text-slate-300">{person.email}</p>
                        ) : null}
                        <p className="mt-1 text-xs text-slate-700 dark:text-slate-200">
                          {person.campaign_id
                            ? `${person.campaign_name || 'Unnamed campaign'}${person.campaign_step ? ` — ${person.campaign_step}` : ''} (${person.campaign_status || 'unknown status'})`
                            : 'Held back, but no active campaign was found for this person.'}
                        </p>
                        {person.campaign_id ? (
                          <p className="mt-0.5 font-mono text-[11px] text-slate-500 dark:text-slate-400">
                            {person.campaign_id}
                            {person.campaign_next_run_at ? ` · next run ${formatDate(person.campaign_next_run_at)}` : ''}
                          </p>
                        ) : null}
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              <div className="rounded-xl border border-slate-200 bg-white px-4 py-4 dark:border-[#343441] dark:bg-[#11161d]">
                <p className="text-sm font-medium text-slate-900 dark:text-white">Recipient sample</p>
                <div className="mt-3 max-h-[260px] space-y-2 overflow-y-auto">
                  {previewRecipients.length ? previewRecipients.map((recipient) => (
                    <div key={recipient} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 dark:border-[#343441] dark:bg-[#16161a] dark:text-slate-200">
                      {recipient}
                    </div>
                  )) : (
                    <p className="text-sm text-slate-500 dark:text-slate-300">No recipients found for this preview.</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-violet-200 bg-violet-50/70 px-4 py-4 dark:border-violet-500/20 dark:bg-[#241b32]">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-violet-700 dark:text-violet-300" />
                  <p className="text-sm font-semibold text-slate-900 dark:text-white">Schedule this reviewed email</p>
                </div>
                <p className="mt-1 text-xs leading-5 text-slate-600 dark:text-slate-300">
                  The time uses your browser&apos;s local time and must be at least five minutes from now. Scheduling does not send immediately.
                </p>
                <label className="mt-3 block text-xs font-medium text-slate-700 dark:text-slate-200" htmlFor="email-schedule-at">
                  Send at
                </label>
                <input
                  id="email-schedule-at"
                  type="datetime-local"
                  value={emailScheduleAt}
                  onChange={(event) => setEmailScheduleAt?.(event.target.value)}
                  className="mt-1 w-full rounded-lg border border-violet-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm dark:border-violet-400/30 dark:bg-[#16161a] dark:text-white lg:max-w-xs"
                />
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={handleScheduleEmail}
                disabled={emailScheduling || emailTestSending || sending || previewingEmail || hasPlaceholderWarnings || !emailScheduleAt || !previewRecipientCount}
                className="shrink-0 gap-2 border-violet-300 bg-white text-violet-900 hover:bg-violet-50 dark:border-violet-400/30 dark:bg-[#16161a] dark:text-violet-200"
              >
                {emailScheduling ? <Loader2 className="h-4 w-4 animate-spin" /> : <Clock className="h-4 w-4" />}
                {emailScheduling ? 'Scheduling...' : 'Schedule email'}
              </Button>
            </div>
            {scheduledEmailsLoading ? (
              <div className="mt-4 flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading scheduled emails...
              </div>
            ) : scheduledEmails.length ? (
              <div className="mt-4 space-y-2">
                <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">Recent schedules</p>
                {scheduledEmails.map((scheduled) => (
                  <div key={scheduled.schedule_id} className="flex flex-col gap-2 rounded-lg border border-violet-200 bg-white px-3 py-2.5 dark:border-violet-400/20 dark:bg-[#16161a] sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-slate-900 dark:text-white">{scheduled.subject || 'Untitled email'}</p>
                      <p className="mt-1 text-xs text-slate-600 dark:text-slate-300">
                        {scheduled.status} · {formatDate(scheduled.scheduled_for)} · {AUDIENCE_LABELS[scheduled.recipient_type] || scheduled.recipient_type}
                      </p>
                    </div>
                    {scheduled.status === 'scheduled' ? (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => handleCancelScheduledEmail?.(scheduled.schedule_id)}
                        className="h-8 shrink-0 border-rose-200 bg-white px-3 text-xs text-rose-700 hover:bg-rose-50 dark:border-rose-500/30 dark:bg-[#22222b] dark:text-rose-300"
                      >
                        Cancel
                      </Button>
                    ) : null}
                  </div>
                ))}
              </div>
            ) : (
              <p className="mt-4 text-xs text-slate-600 dark:text-slate-300">No scheduled emails in this zone yet.</p>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setEmailPreviewOpen(false)} className="border-slate-200 bg-white text-slate-900 hover:bg-slate-50 dark:border-[#343441] dark:bg-[#16161a] dark:text-white">
              Back to Edit
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={handleSendTestEmail}
              disabled={emailTestSending || sending || previewingEmail || hasPlaceholderWarnings || !emailContent.subject?.trim() || !emailContent.body?.trim()}
              title="Send this exact reviewed email only to your signed-in admin email"
              className="gap-2 border-cyan-300 bg-white text-cyan-900 hover:bg-cyan-50 dark:border-aqua/30 dark:bg-[#16161a] dark:text-aqua"
            >
              {emailTestSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
              {emailTestSending ? 'Sending test...' : 'Send test to me'}
            </Button>
            <Button onClick={handleSendEmail} disabled={sendBlocked} className="bg-aqua hover:bg-aqua-dark text-charcoal gap-2">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {sending
                ? 'Sending...'
                : hasPlaceholderWarnings
                  ? 'Fix placeholders before sending'
                  : singleEmailDeliveryIssue
                    ? 'Blocked by delivery warning'
                    : 'Send Email'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card className="border border-green-200 bg-green-50/80 p-6 dark:border-green-500/30 dark:bg-green-500/10">
        <h3 className="mb-2 flex items-center gap-2 text-lg font-semibold text-green-700 dark:text-green-400">
          <Check className="w-5 h-5" />
          Email Service Connected
        </h3>
        <p className="mb-3 text-sm text-slate-700 dark:text-slate-200">Your email service is configured and ready to send.</p>
        <div className="flex items-center justify-between rounded-xl bg-white p-4 dark:bg-[#16161a]">
          <div>
            <p className="font-medium text-slate-900 dark:text-white">Resend</p>
            <p className="text-sm text-slate-500 dark:text-slate-300">Sending from: hello@notifications.dad-partners.com</p>
          </div>
          <Badge className="border-green-200 bg-green-100 text-green-700 dark:border-green-500/30 dark:bg-green-500/10 dark:text-green-400">
            <Check className="w-3 h-3 mr-1" /> Connected
          </Badge>
        </div>
      </Card>

      <Card className="border-slate-200 bg-white p-6 dark:border-[#343441] dark:bg-[#1e1e24]">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h3 className="flex items-center gap-2 text-lg font-semibold text-slate-900 dark:text-white">
              <AlertTriangle className="h-5 w-5 text-amber-500 dark:text-amber-300" />
              Delivery Issues Snapshot
            </h3>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600 dark:text-slate-200">
              Import the Resend CSV exports for bounced, suppressed, complained, and delayed emails. The Command Center rooms will use this snapshot to show who needs a corrected address right now.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              multiple
              className="hidden"
              onChange={(event) => {
                handleImportDeliveryIssues?.(event.target.files);
                event.target.value = '';
              }}
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={deliveryIssuesImporting}
              className="gap-2 border-slate-200 bg-white text-slate-900 hover:bg-slate-50 dark:border-[#343441] dark:bg-[#16161a] dark:text-white"
            >
              {deliveryIssuesImporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4" />}
              {deliveryIssuesImporting ? 'Importing...' : 'Import Resend CSVs'}
            </Button>
          </div>
        </div>

        <div className="mt-5 grid gap-3 md:grid-cols-4">
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-[#343441] dark:bg-[#16161a]">
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">Current issues</p>
            <p className="mt-3 text-2xl font-semibold text-slate-900 dark:text-white">
              {deliveryIssuesLoading ? '—' : issueCount}
            </p>
            <p className="mt-1 text-sm text-slate-700 dark:text-slate-200">Emails currently blocking or risking delivery</p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-[#343441] dark:bg-[#16161a]">
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">Bounced</p>
            <p className="mt-3 text-2xl font-semibold text-slate-900 dark:text-white">{deliveryIssueCounts?.bounced ?? '—'}</p>
            <p className="mt-1 text-sm text-slate-700 dark:text-slate-200">Need corrected addresses before assuming they landed</p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-[#343441] dark:bg-[#16161a]">
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">Suppressed</p>
            <p className="mt-3 text-2xl font-semibold text-slate-900 dark:text-white">{deliveryIssueCounts?.suppressed ?? '—'}</p>
            <p className="mt-1 text-sm text-slate-700 dark:text-slate-200">Resend is holding these until the address is cleaned up</p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-[#343441] dark:bg-[#16161a]">
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">Complained / delayed</p>
            <p className="mt-3 text-2xl font-semibold text-slate-900 dark:text-white">{(deliveryIssueCounts?.complained || 0) + (deliveryIssueCounts?.delivery_delayed || 0)}</p>
            <p className="mt-1 text-sm text-slate-700 dark:text-slate-200">Manual follow-up or watch before more automation</p>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 dark:border-[#343441] dark:bg-[#1b1b23] dark:text-slate-200">
          <span>
            {deliveryIssuesLastImportedAt
              ? `Last delivery update ${formatDate(deliveryIssuesLastImportedAt)}`
              : 'No delivery-issue snapshot or webhook updates yet'}
          </span>
          <span>
            {issueCount > 0
              ? `${issueCount} current bad-address warnings are ready for the rooms`
              : 'Importing a CSV snapshot will light up the room warnings'}
          </span>
        </div>
      </Card>

      <Card className="border-slate-200 bg-white p-6 dark:border-[#343441] dark:bg-[#1e1e24]">
        <h3 className="mb-4 text-lg font-semibold text-slate-900 dark:text-white">Recent Emails</h3>
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-6 h-6 text-aqua animate-spin" />
          </div>
        ) : recentEmails.length === 0 ? (
          <div className="text-center py-8">
            <Mail className="mx-auto mb-3 h-10 w-10 text-slate-400 dark:text-gray-600" />
            <p className="text-slate-500 dark:text-slate-300">No emails sent yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {recentEmails.map((email, idx) => (
              <div key={email.email_id || idx} className="flex items-center justify-between rounded-xl bg-slate-50 p-4 dark:bg-[#16161a]">
                <div>
                  <p className="font-medium text-slate-900 dark:text-white">{email.subject}</p>
                  <p className="text-sm text-slate-600 dark:text-slate-300">
                    {email.recipient_type === 'all' ? 'All Members' : email.tier || email.recipient_type} • {formatDate(email.sent_at)}
                  </p>
                </div>
                <Badge className={email.status === 'sent'
                  ? 'border-green-200 bg-green-100 text-green-700 dark:border-green-500/30 dark:bg-green-500/10 dark:text-green-400'
                  : 'border-yellow-200 bg-yellow-100 text-yellow-800 dark:border-yellow-500/30 dark:bg-yellow-500/10 dark:text-yellow-400'}>
                  {email.status === 'sent' ? <Check className="w-3 h-3 mr-1" /> : <Clock className="w-3 h-3 mr-1" />}
                  {email.status === 'sent' ? `Sent to ${email.recipient_count}` : `Queued for ${email.recipient_count}`}
                </Badge>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

export default EmailTab;
